import React, { useEffect, useRef, useState } from"react";
import { connect, ConnectedProps } from"react-redux";
import { Dispatch, nanoid } from"@reduxjs/toolkit";
import { isEmpty } from"lodash";
// store
import AppStore from "@/app-store";
import Store from"./store";
// actions
import MessagePublisherAction from "@/core/actions/common/MessagePublisherAction";
import ContactUsServiceAction from "@/cms/actions/services/ContactUsServiceAction";
// types
import TClientLanguage from "@/core/types/TClientLanguage";
import TBase from "@/cms/types/TBase";
// types
import TValidations from "@/cms/types/TValidations";
import TContactForm from"./type";
// enums
import EContactUsStatus from "@/cms/enums/EContactUsStatus";
// utilities
import { metaDataLanguageVersion } from "@/utilities/LanguageVersionSelectorShortcut";


const unqKey = nanoid(12);

const mapStateToProps = ( state:any, props:TContactForm ) => {
    console.log("ContactForm.mapStateToProps:", state);

    const webApplicationLanguage:TClientLanguage    = state.CoreStore.language?.payload;
    const webApplicationLanguageCode:string         = webApplicationLanguage?.code;
    const webApplicationLanguageISOCode:string      = state.CoreStore.languageCode?.payload;

    const isStoreExists:boolean = !isEmpty(state?.[unqKey]);

    const contactUsPostPayload:TBase                = state[unqKey]?.ContactUsStore?.post?.payload;
    const contactUsPostError:TValidations[]|null    = state[unqKey]?.ContactUsStore?.post?.error?.value;
    const contactUsPostIsLoading:boolean            = state[unqKey]?.ContactUsStore?.post?.isLoading;

    return {
        webApplicationLanguage,
        webApplicationLanguageCode,
        webApplicationLanguageISOCode,

        isStoreExists,

        contactUsPostPayload,
        contactUsPostError,
        contactUsPostIsLoading
    }
}

const mapDispatchToProps = ( dispatch:Dispatch, props:TContactForm ) => {
    console.log("ContactForm.mapStateToProps:", props);

    const contactUsServiceAction = new ContactUsServiceAction(dispatch, unqKey);
    const messagePublisherAction = new MessagePublisherAction(dispatch);

    return {
        _post: ( payload:any, webApplicationLanguageCode:string ) => {
            contactUsServiceAction.post({ servicePayload: { body: { ...payload, status: EContactUsStatus.NEW }, language: webApplicationLanguageCode } })
        },
        
        _showMessage: ( message:string ) => { 
            messagePublisherAction.showSuccessMessage(message);
        },

        _clean: () => {
            contactUsServiceAction.postClean();
        }
    }
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;


const ContactForm = ( props: TContactForm<PropsFromRedux> ) => {
    console.log("ContactForm.rendered:", props);

    useEffect(
        () => {
            AppStore.reducerManager.add(unqKey, Store(unqKey));
            return () => {
                AppStore.reducerManager.remove(unqKey);
            }
        }, []
    )

    const [formNameSurnamePlaceholder] = useState(metaDataLanguageVersion("formNameSurnamePlaceholder", props.languageVersions));
    const [formEmailPlaceholder] = useState(metaDataLanguageVersion("formEmailPlaceholder", props.languageVersions));
    const [formSubjectPlaceholder] = useState(metaDataLanguageVersion("formSubjectPlaceholder", props.languageVersions));
    const [formMessagePlaceholder] = useState(metaDataLanguageVersion("formMessagePlaceholder", props.languageVersions));

    const [formSubmitButtonString] = useState(metaDataLanguageVersion("formSubmitButtonString", props.languageVersions));
    const [formSubmitButtonSendingString] = useState(metaDataLanguageVersion("formSubmitButtonSendingString", props.languageVersions));

    const [formDefaultSubject] = useState(metaDataLanguageVersion("formDefaultSubject", props.languageVersions));
    const [formDefaultMessage] = useState(metaDataLanguageVersion("formDefaultMessage", props.languageVersions));
    const [messageHasBeenSentString] = useState(metaDataLanguageVersion("messageHasBeenSentString", props.languageVersions));

    const [disabled, setDisabled] = useState(false);

    const nameSurnameRef = useRef<any>(null);
    const emailRef = useRef<any>(null);
    const messageRef = useRef<any>(null);
    const subjectRef = useRef<any>(null);

    useEffect(
        () => {
            return () => {
                props._clean();
            }
        }, []
    )

    useEffect(
        () => {
            if ( !isEmpty(props.contactUsPostPayload?.id) ) {
                nameSurnameRef.current.value ="";
                emailRef.current.value ="";
                messageRef.current.value ="";
                subjectRef.current.value ="";

                props._clean();
                setDisabled(false);
                props._showMessage(messageHasBeenSentString);
            }
        }, [props.contactUsPostPayload]
    )

    useEffect(
        () => {
            if ( !isEmpty(props.contactUsPostError) ) {
                setDisabled(false);
            }
        }, [ props.contactUsPostError ]
    )
    
    const onSubmit = ( event:React.SyntheticEvent ) => { 
        event.preventDefault();
        setDisabled(true);

        const target = event.target as typeof event.target & {
            nameSurname: { value: string },
            subject: { value: string },
            email: { value: string },
            message: { value: string },
        };

        const messageList = props.addURLToMessage ? 
            [
                `URL: ${window.location.href}`,
                `Message:\n ${target.message.value}`,
            ] : 
            [ 
                `Message:\n ${target.message.value}` 
            ];

        const payload:any = {
            nameSurname: target.nameSurname.value,
            email: target.email.value,
            title: target.subject.value,
            message: messageList.join("\n")
        }

        props._post(payload, props.webApplicationLanguageCode);
    }

    return (
        <form method="post" className="contact-form-items" id="contact-form" onSubmit={onSubmit}>
            <div className="row g-4">
                <div className="col-lg-6" data-aos-duration="800" data-aos="fade-up" data-aos-delay="300">
                    <div className="form-clt">
                        <input ref={nameSurnameRef} type="text" name="nameSurname" id="nameSurname" disabled={disabled||props.contactUsPostIsLoading} placeholder={formNameSurnamePlaceholder} />
                    </div>
                </div>
                <div className="col-lg-6 " data-aos-duration="800" data-aos="fade-up" data-aos-delay="500">
                    <div className="form-clt">
                        <input ref={emailRef} type="text" name="email" id="email" disabled={disabled||props.contactUsPostIsLoading} placeholder={formEmailPlaceholder} />
                    </div>
                </div>
                <div className="col-lg-12 " data-aos-duration="800" data-aos="fade-up" data-aos-delay="700">
                    <div className="form-clt">
                        <input ref={subjectRef} type="text" name="subject" id="subject" disabled={disabled||props.contactUsPostIsLoading} placeholder={formDefaultSubject||formSubjectPlaceholder} />
                    </div>
                </div>
                <div className="col-lg-12 " data-aos-duration="800" data-aos="fade-up" data-aos-delay="900">
                    <div className="form-clt">
                        <textarea ref={messageRef} name="message" id="message" disabled={disabled||props.contactUsPostIsLoading} placeholder={formDefaultMessage||formMessagePlaceholder}></textarea>
                    </div>
                </div>
                <div className="col-lg-7 " data-aos-duration="800" data-aos="fade-up" data-aos-delay="900">
                    <button type="submit" className="theme-btn" disabled={disabled||props.contactUsPostIsLoading}> 
                        {props.contactUsPostIsLoading ? formSubmitButtonSendingString:formSubmitButtonString}{" "}
                        <i className="fa-solid fa-arrow-right-long"></i>
                    </button>
                </div>
            </div>
        </form>
    )
}

const Component = React.memo(ContactForm, ( prevProps:TContactForm<PropsFromRedux>, nextProps:TContactForm<PropsFromRedux> ) => {
    console.log("ContactForm.memo", { prevProps, nextProps });
    return !(
        prevProps.contactUsPostPayload?.id !== nextProps.contactUsPostPayload?.id
        || prevProps.contactUsPostError !== nextProps.contactUsPostError
        || prevProps.isStoreExists !== nextProps.isStoreExists
    );
});

export default connector(Component);
